import React, { useEffect, useState } from "react";
import useDebounce from "../../hooks/useDebounce";
import * as Collapsible from "@radix-ui/react-collapsible";
import {
  Store,
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  Phone,
  Mail,
  Globe,
  MapPin,
  BadgePercent,
  Edit,
  Trash2,
  Eye,
} from "lucide-react";
import * as Switch from "@radix-ui/react-switch";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  fetchShops, 
  updateShopStatus,
  updateShopContact,
  updateShopDiscount,
  updateShopGallery,
  updateBusinessInfo,
  updateBusinessSettings,
  updateBusinessLocation,
  updateBusinessCategoriesServices,
  createUserAccount
} from "../../store/slices/shopsSlice";
import {
  fetchCategories,
  fetchStates,
  fetchDistrictsByState,
  fetchTalukasByDistrict,
  fetchSubcategoriesByCategory,
  fetchServicesByCategory,
  fetchSubscriptionPackages,
  fetchPaymentModes,
} from "../../store/slices/commonApiSlice";
import ReusableModal, { MODAL_TYPES } from "../../components/ReusableModal";
import SubSectionEditForm from "../../components/SubSectionEditForm";
import { 
  ContactEditForm, 
  DiscountEditForm, 
  GalleryEditForm,
  BusinessInfoEditForm,
  BusinessSettingsEditForm,
  LocationEditForm,
  CategoriesServicesEditForm 
} from "../../components/BusinessEditModals";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";
import ShowMoreModal from "../../components/ui/ShowMoreModal";
import Pagination from "../../components/tables/Pagination";
export default function ViewBussinessPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterTaluka, setFilterTaluka] = useState("all");

  // Modal state management
  const [modalState, setModalState] = useState({
    open: false,
    type: null,
    data: null,
    subSection: null, // For sub-section edits like contact, discount, etc.
    loading: false, // Track loading state for modal actions
  });

  const {
    shops = [],
    pagination,
    shopChangeStatus,
    shopContactUpdateStatus,
    shopDiscountUpdateStatus,
    shopGalleryUpdateStatus,
    businessInfoUpdateStatus,
    businessSettingsUpdateStatus,
    businessLocationUpdateStatus,
    businessCategoriesServicesUpdateStatus,
    createUserAccountStatus,
    loading,
  } = useSelector((state) => state.shop);

  const {
    categories = [],
    subcategories = [],
    subscriptionPackages = [],
    states = [],
    districts = [],
    talukas = [],
    categoryServices = [],
    paymentModes = [],
  } = useSelector((state) => state.common);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const itemsPerPage = 10;

  // Calculate which backend page we need based on current frontend page
  const backendPageSize = 100;
  const startRecord = (currentPage - 1) * itemsPerPage;
  const backendPage = Math.floor(startRecord / backendPageSize) + 1;

  useEffect(() => {
    // Fetch the backend page that contains the records we need
    dispatch(fetchShops({ page: backendPage, per_page: backendPageSize }));
    dispatch(fetchCategories());
    dispatch(fetchStates());
    dispatch(fetchSubscriptionPackages());
    dispatch(fetchPaymentModes());
  }, [dispatch, backendPage]);

  // Function to fetch related data when opening edit modals
  const fetchRelatedData = (shop, section) => {
    if (section === 'location' || section === 'business_settings') {
      // Fetch districts and talukas based on shop's location
      if (shop.state?.uuid) {
        dispatch(fetchDistrictsByState(shop.state.uuid));
      }
      if (shop.district?.uuid) {
        dispatch(fetchTalukasByDistrict(shop.district.uuid));
      }
    }
    
    if (section === 'categories_services' || section === 'business_settings') {
      // Fetch subcategories and services based on shop's category
      if (shop.category?.uuid) {
        dispatch(fetchSubcategoriesByCategory(shop.category.slug));
        dispatch(fetchServicesByCategory(shop.category.slug));
      }
    }
  };

  // Check if any filters are active
  const hasActiveFilters = 
    debouncedSearch !== "" ||
    filterStatus !== "all" ||
    filterCategory !== "all" ||
    filterState !== "all" ||
    filterDistrict !== "all" ||
    filterTaluka !== "all";

  const filteredShops = shops.filter((shop) => {
    const matchesSearch =
      shop.business_name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      shop.address.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      shop.username.toLowerCase().includes(debouncedSearch.toLowerCase());
    const matchesFilter =
      filterStatus === "all" ||
      (shop.is_trending ? "active" : "inactive") === filterStatus;
    const matchesCategory =
      filterCategory === "all" || shop.category?.uuid === filterCategory;
    const matchesState =
      filterState === "all" || shop.state?.uuid === filterState;
    const matchesDistrict =
      filterDistrict === "all" || shop.district?.uuid === filterDistrict;
    const matchesTaluka =
      filterTaluka === "all" || shop.taluka?.uuid === filterTaluka;
    return matchesSearch && matchesFilter && matchesCategory && matchesState && matchesDistrict && matchesTaluka;
  });

  // Client-side pagination from backend data
  // When filters are active, use filtered count for accurate page count
  // When no filters, use server total so pagination reflects all records
  const hasActiveClientFilters =
    debouncedSearch !== "" ||
    filterStatus !== "all" ||
    filterCategory !== "all" ||
    filterState !== "all" ||
    filterDistrict !== "all" ||
    filterTaluka !== "all";

  const totalRecords = hasActiveClientFilters
    ? filteredShops.length
    : (pagination?.total || filteredShops.length);
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  
  // Calculate which records to show from the current backend page
  const backendStartIndex = (backendPage - 1) * backendPageSize;
  const localStartIndex = startRecord - backendStartIndex;
  const localEndIndex = localStartIndex + itemsPerPage;
  const paginatedShops = filteredShops.slice(localStartIndex, localEndIndex);

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-red-100 text-red-800",
    };
    return statusStyles[status] || "bg-gray-100 text-gray-800";
  };

  const handlePageChange = (page) => {
    navigate(`?page=${page}`);
  };

  // Handle state change to fetch districts
  const handleStateChange = (stateUuid) => {
    setFilterState(stateUuid);
    setFilterDistrict("all");
    setFilterTaluka("all");
    if (stateUuid !== "all") {
      dispatch(fetchDistrictsByState(stateUuid));
    }
  };

  // Handle district change to fetch talukas
  const handleDistrictChange = (districtUuid) => {
    setFilterDistrict(districtUuid);
    setFilterTaluka("all");
    if (districtUuid !== "all") {
      dispatch(fetchTalukasByDistrict(districtUuid));
    }
  };

  // Modal handlers
  const openModal = (type, data, subSection = null) => {
    let entityType = 'Shop';
    
    // Set custom entity type for generate account
    if (subSection === 'generate_account') {
      entityType = 'Generate User Account';
    } else if (subSection) {
      entityType = `${subSection.charAt(0).toUpperCase() + subSection.slice(1)} Information`;
    }
    
    // Fetch related data for dropdowns
    if (subSection && subSection !== 'generate_account') {
      fetchRelatedData(data, subSection);
    }
    
    setModalState({
      open: true,
      type,
      data: { ...data, entityType }, // Add entityType for generic modal titles
      subSection,
      loading: false,
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      type: null,
      data: null,
      subSection: null,
      loading: false,
    });
  };

  const setModalLoading = (loading) => {
    setModalState(prev => ({
      ...prev,
      loading
    }));
  };

  const handleStatusChange = async (shopData) => {
    setModalLoading(true);
    try {
      await dispatch(
        updateShopStatus({
          uuid: shopData?.uuid,
          status: shopData?.status ? "disable" : "enable",
        })
      );
      closeModal();
    } catch (error) {
      setModalLoading(false);
    }
  };

  const handleEditShop = (shop) => {
    // console.log(shop)
    // Navigate to AddShopPage with shop UUID for editing
    navigate(`/shops/add?edit=${shop.uuid}`);
  };

  // Function to trigger form submission from modal
  const handleModalConfirm = () => {
    if (modalState.type === MODAL_TYPES.ENABLE_DISABLE) {
      handleStatusChange(modalState.data);
    } else if (modalState.type === MODAL_TYPES.DELETE) {
      handleDeleteShop(modalState.data);
    } else if (modalState.type === MODAL_TYPES.CONFIRM && modalState.subSection === 'generate_account') {
      handleCreateUserAccount(modalState.data);
    } else {
      handleEditSubSection(modalState.data);
    }
  };

  const handleDeleteShop = async (shopData) => {
    setModalLoading(true);
    try {
      // TODO: Implement shop delete API call
      console.log('Deleting shop:', shopData.uuid);
      // await dispatch(deleteShop({ uuid: shopData.uuid }));

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      showApiSuccess("Shop deleted successfully");
      closeModal();
    } catch (error) {
      setModalLoading(false);
      showApiError(null, "Failed to delete shop");
    }
  };

  const handleCreateUserAccount = async (shop) => {
    setModalLoading(true);
    try {
      await dispatch(createUserAccount(shop.uuid)).unwrap();
      showApiSuccess("User account created successfully");
      closeModal();
      dispatch(fetchShops({ page: backendPage, per_page: backendPageSize }));
    } catch (error) {
      setModalLoading(false);
      showApiError(error, error || "Failed to create user account");
    }
  };

  const handleEditSubSection = async (subSectionData) => {
    if (!modalState.data?.uuid) {
      showApiError(null, "Business ID not found");
      return;
    }

    setModalLoading(true);
    try {
      const shopUuid = modalState.data.uuid;
      
      switch (modalState.subSection) {
        case 'contact':
          await dispatch(updateShopContact({ 
            uuid: shopUuid, 
            data: subSectionData 
          })).unwrap();
          break;
        case 'discount':
          await dispatch(updateShopDiscount({ 
            uuid: shopUuid, 
            data: subSectionData 
          })).unwrap();
          break;
        case 'gallery':
          await dispatch(updateShopGallery({ 
            uuid: shopUuid, 
            data: subSectionData 
          })).unwrap();
          break;

        case 'business_info':
          await dispatch(updateBusinessInfo({ 
            uuid: shopUuid, 
            data: subSectionData 
          })).unwrap();
          break;
        case 'business_settings':
          await dispatch(updateBusinessSettings({ 
            uuid: shopUuid, 
            data: subSectionData 
          })).unwrap();
          break;
        case 'location':
          await dispatch(updateBusinessLocation({ 
            uuid: shopUuid, 
            data: subSectionData 
          })).unwrap();
          break;
        case 'categories_services':
          await dispatch(updateBusinessCategoriesServices({ 
            uuid: shopUuid, 
            data: subSectionData 
          })).unwrap();
          break;
        default:
          throw new Error(`Unknown section type: ${modalState.subSection}`);
      }

      const sectionName = modalState.subSection.charAt(0).toUpperCase() + modalState.subSection.slice(1);
      showApiSuccess(`${sectionName} updated successfully`);
      closeModal();
      
      // Refresh the shops list to show updated data
      dispatch(fetchShops({ page: backendPage, per_page: backendPageSize }));
    } catch (error) {
      setModalLoading(false);
      const errorMessage = error.message || `Failed to update ${modalState.subSection}`;
      showApiError(error, error);
    }
  };

  useEffect(() => {
    if (shopChangeStatus === "success") {
      showApiSuccess("Status updated successfully");
    } else if (shopChangeStatus === "failed") {
      showApiError(null, "Status update failed");
    }
  }, [shopChangeStatus]);

  // Handle contact update status
  useEffect(() => {
    if (shopContactUpdateStatus === "success") {
      showApiSuccess("Contact information updated successfully");
    } else if (shopContactUpdateStatus === "failed") {
      showApiError(null, "Failed to update contact information");
    }
  }, [shopContactUpdateStatus]);

  // Handle discount update status
  useEffect(() => {
    if (shopDiscountUpdateStatus === "success") {
      showApiSuccess("Discount information updated successfully");
    } else if (shopDiscountUpdateStatus === "failed") {
      showApiError(null, "Failed to update discount information");
    }
  }, [shopDiscountUpdateStatus]);

  // Handle gallery update status
  useEffect(() => {
    if (shopGalleryUpdateStatus === "success") {
      showApiSuccess("Gallery updated successfully");
    } else if (shopGalleryUpdateStatus === "failed") {
      showApiError(null, "Failed to update gallery");
    }
  }, [shopGalleryUpdateStatus]);



  // Handle business info update status
  useEffect(() => {
    if (businessInfoUpdateStatus === "success") {
      showApiSuccess("Business information updated successfully");
    } else if (businessInfoUpdateStatus === "failed") {
      showApiError(null, "Failed to update business information");
    }
  }, [businessInfoUpdateStatus]);

  // Handle business settings update status
  useEffect(() => {
    if (businessSettingsUpdateStatus === "success") {
      showApiSuccess("Business settings updated successfully");
    } else if (businessSettingsUpdateStatus === "failed") {
      showApiError(null, "Failed to update business settings");
    }
  }, [businessSettingsUpdateStatus]);

  // Handle business location update status
  useEffect(() => {
    if (businessLocationUpdateStatus === "success") {
      showApiSuccess("Business location updated successfully");
    } else if (businessLocationUpdateStatus === "failed") {
      showApiError(null, "Failed to update business location");
    }
  }, [businessLocationUpdateStatus]);

  // Handle business categories and services update status
  useEffect(() => {
    if (businessCategoriesServicesUpdateStatus === "success") {
      showApiSuccess("Categories and services updated successfully");
    } else if (businessCategoriesServicesUpdateStatus === "failed") {
      showApiError(null, "Failed to update categories and services");
    }
  }, [businessCategoriesServicesUpdateStatus]);

  // Handle create user account status
  useEffect(() => {
    if (createUserAccountStatus === "success") {
      showApiSuccess("User account created successfully");
    } else if (createUserAccountStatus === "failed") {
      showApiError(null, "Failed to create user account");
    }
  }, [createUserAccountStatus]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">All Business</h2>
          </div>
          <button
            onClick={() => navigate('/business/add')}
            className="mt-4 sm:mt-0 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Store className="h-4 w-4" />
            <span>Add New Shop</span>
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search shops..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="active">Trending</option>
                <option value="inactive">Not Trending</option>
              </select>
            </div>
          </div>

          {/* Category and Location Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Category Filter */}
            <div className="relative">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category.uuid} value={category.uuid}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* State Filter */}
            <div className="relative">
              <select
                value={filterState}
                onChange={(e) => handleStateChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All States</option>
                {states.map((state) => (
                  <option key={state.uuid} value={state.uuid}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            {/* District Filter */}
            <div className="relative">
              <select
                value={filterDistrict}
                onChange={(e) => handleDistrictChange(e.target.value)}
                disabled={filterState === "all"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="all">All Districts</option>
                {districts.map((district) => (
                  <option key={district.uuid} value={district.uuid}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Taluka Filter */}
            <div className="relative">
              <select
                value={filterTaluka}
                onChange={(e) => setFilterTaluka(e.target.value)}
                disabled={filterDistrict === "all"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="all">All Talukas</option>
                {talukas.map((taluka) => (
                  <option key={taluka.uuid} value={taluka.uuid}>
                    {taluka.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Shops Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12" />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Shop
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Sub Categories
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Contact No.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            {paginatedShops.length > 0 ? (
              paginatedShops.map((shop, idx) => (
                <ExpandableRow
                  idx={idx}
                  key={shop.uuid}
                  shop={shop}
                  getStatusBadge={getStatusBadge}
                  onOpenModal={openModal}
                  onEditShop={handleEditShop}
                  onCreateUserAccount={handleCreateUserAccount}
                />
              ))
            ) : (
              <tbody>
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <Store className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <p className="text-lg font-medium mb-2">No shops found</p>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      </div>

      {/* Filter Warning */}
      {hasActiveFilters && totalPages > 1 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Filters are applied to all data. 
            Showing {paginatedShops.length} of {filteredShops.length} filtered results on this page.
          </p>
        </div>
      )}

      {/* Pagination - Only show if backend returns proper pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Reusable Modal */}
      <ReusableModal
              open={modalState.open}
              onOpenChange={closeModal}
              type={modalState.type}
              data={modalState.data}
              message={
                modalState.subSection === 'generate_account'
                  ? `Are you sure you want to generate a user account for "${modalState.data?.business_name}"? Login credentials will be created for this business.`
                  : undefined
              }
              onConfirm={handleModalConfirm}
              loading={modalState.loading}
              showActions={modalState.type !== MODAL_TYPES.CUSTOM}
              size={modalState.subSection && modalState.subSection !== 'generate_account' ? "large" : "default"}
            >
              {modalState.subSection && modalState.subSection === 'contact' && (
                <ContactEditForm
                  data={modalState.data}
                  onSave={handleEditSubSection}
                  loading={modalState.loading}
                />
              )}
              {modalState.subSection && modalState.subSection === 'discount' && (
                <DiscountEditForm
                  data={modalState.data}
                  onSave={handleEditSubSection}
                  loading={modalState.loading}
                />
              )}
              {modalState.subSection && modalState.subSection === 'gallery' && (
                <GalleryEditForm
                  data={modalState.data}
                  onSave={handleEditSubSection}
                  loading={modalState.loading}
                />
              )}

              {modalState.subSection && modalState.subSection === 'business_info' && (
                <BusinessInfoEditForm
                  data={modalState.data}
                  onSave={handleEditSubSection}
                  loading={modalState.loading}
                />
              )}
              {modalState.subSection && modalState.subSection === 'business_settings' && (
                <BusinessSettingsEditForm
                  data={modalState.data}
                  onSave={handleEditSubSection}
                  loading={modalState.loading}
                  subscriptionPackages={subscriptionPackages}
                  paymentModes={paymentModes}
                  dispatch={dispatch}
                />
              )}
              {modalState.subSection && modalState.subSection === 'location' && (
                <LocationEditForm
                  data={modalState.data}
                  onSave={handleEditSubSection}
                  loading={modalState.loading}
                  states={states}
                  districts={districts}
                  talukas={talukas}
                  dispatch={dispatch}
                />
              )}
              {modalState.subSection && modalState.subSection === 'categories_services' && (
                <CategoriesServicesEditForm
                  data={modalState.data}
                  onSave={handleEditSubSection}
                  loading={modalState.loading}
                  categories={categories}
                  subCategories={subcategories}
                  services={categoryServices}
                  dispatch={dispatch}
                />
              )}
            </ReusableModal>

    </div>
  );
}

function ExpandableRow({idx, shop, getStatusBadge, onOpenModal, onEditShop, onCreateUserAccount }) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} asChild>
      <tbody>
        <tr className="hover:bg-gray-50 transition-colors">
          <td className="px-2">
            <Collapsible.Trigger asChild>
              <button className="p-1 rounded hover:bg-gray-100">
                {open ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            </Collapsible.Trigger>
          </td>
          <td className="px-6 py-4">{shop.business_name}</td>
          <td className="px-6 py-4">{shop.category?.name}</td>
          <td className="px-6 py-4">
            <ShowMoreModal
              items={shop.subcategories || []}
              initialCount={2}
              renderItem={(subcategory) => (
                <span className="text-sm text-gray-900">{subcategory?.name}</span>
              )}
              title="All Sub Categories"
              emptyMessage="No subcategories"
            />
          </td>
          <td className="px-6 py-4">{shop?.contact_numbers[0]}</td> 
          

          {/* <td className="px-6 py-4">
            <span
              className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(
                shop.is_trending ? "active" : "inactive"
              )}`}
            >
              {shop.is_trending ? "Trending" : "Not Trending"}
            </span>
          </td> */}
          <td className="px-6 py-4 text-sm">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Switch.Root
                id={`shop-status-${shop.uuid}`}
                checked={shop?.status}
                onCheckedChange={() => onOpenModal(MODAL_TYPES.ENABLE_DISABLE, shop)}
                className="w-[42px] h-[25px] bg-gray-200 rounded-full relative shadow-sm 
                     data-[state=checked]:bg-blue-600 outline-none cursor-pointer flex-shrink-0"
              >
                <Switch.Thumb
                  className="block w-[21px] h-[21px] bg-white rounded-full transition-transform 
                       translate-x-0.5 will-change-transform 
                       data-[state=checked]:translate-x-[19px]"
                />
              </Switch.Root>

              <div className="flex items-center">
                {shop.has_user_account ? (
                  <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200 whitespace-nowrap">
                    Account Activated
                  </span>
                ) : (
                  <button
                    onClick={() => onOpenModal(MODAL_TYPES.CONFIRM, shop, 'generate_account')}
                    className="theme-button-gradient px-4 py-1.5 text-xs font-medium rounded-lg transition-all hover:shadow-md whitespace-nowrap"
                    title="Create user account credentials"
                  >
                    Create Account
                  </button>
                )}
                {/* <button
                  onClick={() => console.log('View shop:', shop.uuid)}
                  className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                  title="View Shop"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onEditShop(shop)}
                  className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                  title="Edit Shop"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onOpenModal(MODAL_TYPES.DELETE, shop)}
                  className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                  title="Delete Shop"
                >
                  <Trash2 className="h-4 w-4" />
                </button> */}
              </div>
            </div>
          </td>
        </tr>

        <Collapsible.Content asChild>
          <tr className="max-h-[100px]">
            <td colSpan={6} className="p-4  bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                {/* Business Info */}
                <div className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">Business Info</h4>
                    <button
                      onClick={() => onOpenModal(MODAL_TYPES.CUSTOM, shop, 'business_info')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Edit Business Info"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  <p><strong>Name:</strong> {shop.business_name}</p>
                  <p><strong>Username:</strong> {shop.username}</p>
                  <p><strong>About:</strong> {shop.about_business || 'Not provided'}</p>
                  <div className="flex gap-2 mt-2">
                    {shop.is_trending && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                        Trending
                      </span>
                    )}
                    {shop.is_recently_added && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                        Recently Added
                      </span>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">Contact</h4>
                    <button
                      onClick={() => onOpenModal(MODAL_TYPES.CUSTOM, shop, 'contact')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Edit Contact Info"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  <p>
                    <Phone className="inline h-4 w-4 mr-1" />{" "}
                    {shop.contact_numbers?.join(", ")}
                  </p>
                  <p>
                    <Mail className="inline h-4 w-4 mr-1" /> {shop.email}
                  </p>
                  <p>
                    <Globe className="inline h-4 w-4 mr-1" /> {shop.website}
                  </p>
                  <p>
                    <MapPin className="inline h-4 w-4 mr-1" /> {shop.pincode}
                  </p>
                </div>

                {/* Discount */}
                <div className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">Discount</h4>
                    <button
                      onClick={() => onOpenModal(MODAL_TYPES.CUSTOM, shop, 'discount')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Edit Discount"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  {shop.active_discount ? (
                    <>
                      <p>
                        <BadgePercent className="inline h-4 w-4 mr-1" />{" "}
                        {shop.active_discount.discount_text}
                      </p>
                      <p>Fixed: {shop.active_discount.fixed_discount}%</p>
                      <p>Customer: {shop.active_discount.customer_discount}%</p>
                      <p>
                        Commission: {shop.active_discount.company_commission}%
                      </p>
                    </>
                  ) : (
                    <p>No active discount</p>
                  )}
                </div>

                {/* Gallery */}
                <div className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">Gallery</h4>
                    <button
                      onClick={() => onOpenModal(MODAL_TYPES.CUSTOM, shop, 'gallery')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Edit Gallery"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {shop.gallery_images && shop.gallery_images.length > 0 ? (
                      shop.gallery_images.map((img) => (
                        <img
                          key={img.uuid}
                          src={img.image}
                          alt="shop gallery"
                          className="w-20 h-20 rounded object-cover border"
                        />
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No images uploaded</p>
                    )}
                  </div>
                </div>



                {/* Business Settings */}
                <div className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">Business Settings</h4>
                    <button
                      onClick={() => onOpenModal(MODAL_TYPES.CUSTOM, shop, 'business_settings')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Edit Business Settings"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><strong>Subscription:</strong> {shop.subscription_package?.name || shop.mode || 'Not set'}</p>
                    <p><strong>Payment Modes:</strong> {shop.payment_modes?.join(', ') || 'Not set'}</p>
                    <div className="mt-2">
                      <p><strong>Business Hours:</strong></p>
                      <ShopTimings timings={shop?.business_timings} />
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">Location</h4>
                    <button
                      onClick={() => onOpenModal(MODAL_TYPES.CUSTOM, shop, 'location')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Edit Location"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><strong>State:</strong> {shop.state?.name || 'Not set'}</p>
                    <p><strong>District:</strong> {shop.district?.name || 'Not set'}</p>
                    <p><strong>Taluka:</strong> {shop.taluka?.name || 'Not set'}</p>
                    <p><strong>Address:</strong> {shop.address || 'Not set'}</p>
                    <p><strong>Pincode:</strong> {shop.pincode || 'Not set'}</p>
                    
                    {/* Map Display */}
                    {shop.business_location_map && (
                      <div className="mt-3">
                        <p className="font-semibold mb-2">Map Location:</p>
                        <div className="w-full h-48 border rounded overflow-hidden">
                          <iframe
                            src={shop.business_location_map}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Business Location"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Categories & Services */}
                <div className="bg-white border rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-semibold">Categories & Services</h4>
                    <button
                      onClick={() => onOpenModal(MODAL_TYPES.CUSTOM, shop, 'categories_services')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Edit Categories & Services"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><strong>Category:</strong> {shop.category?.name || 'Not set'}</p>
                    <p><strong>Sub Categories:</strong> {shop.subcategories?.map(cat => cat.name).join(', ') || 'Not set'}</p>
                    {/* <p><strong>Services:</strong> {shop.services?.map(service => service.name).join(', ') || 'Not set'}</p> */}
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </Collapsible.Content>
      </tbody>
    </Collapsible.Root>
  );
}

const ShopTimings = ({ timings }) => {
  console.log('ShopTimings received:', timings);
  
  // Always show all 7 days of the week
  const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const timingsData = timings || {};

  const formatTime = (time) => {
    if (!time) return "--";
    // Check if time is already in 12-hour format (contains AM/PM)
    if (time.includes('AM') || time.includes('PM')) {
      return time;
    }
    // Convert 24-hour format to 12-hour format
    const [hour, minute] = time.split(":").map(Number);
    const suffix = hour >= 12 ? "PM" : "AM";
    const formattedHour = ((hour + 11) % 12) + 1;
    return `${formattedHour}:${minute.toString().padStart(2, "0")} ${suffix}`;
  };

  return (
    <>
      <div className="space-y-2 text-sm">
        {allDays.map((day) => {
          const daySlots = timingsData[day] || [];
          const isClosed = !daySlots || daySlots.length === 0;

          return (
            <div key={day} className="border-b pb-2">
              <div className="font-medium capitalize text-gray-700 mb-1">
                {day}
              </div>
              {isClosed ? (
                <div className="text-red-500 text-xs">Closed</div>
              ) : (
                <div className="space-y-1">
                  {daySlots.map((slot, index) => (
                    <div key={index} className="text-xs text-gray-600">
                      <span className="font-medium">Slot {index + 1}:</span>{' '}
                      {formatTime(slot.open)} - {formatTime(slot.close)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

